import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Backdrop,
  Fade,
  Button,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NomeSobrenomeForm from "./NomeSobrenomeForm";
import {
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  updateUserAttributes,
} from "aws-amplify/auth";
import { EditImagePicker } from "./EditImagePicker";
import { uploadData } from "aws-amplify/storage";
import { toast } from "react-toastify";
import CropperModal from "./CropperModal";

interface EditUserModalProps {
  open: boolean;
  onClose: () => void;
}

export const EditUserModal: React.FC<EditUserModalProps> = ({
  open,
  onClose,
}) => {
  const [image, setImage] = useState<any>();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [tempPreview, setTempPreview] = useState("");
  const [tempFile, setTempFile] = useState<File | null>(null);
  const [tempFirstName, setTempFirstName] = useState("");
  const [tempLastName, setTempLastName] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [selectedImageForCrop, setSelectedImageForCrop] = useState<string>("");

  const handleImageSelect = (file: File, preview: string) => {
    setSelectedImageForCrop("");
    setTempFile(file);
    setSelectedImageForCrop(preview);
    setCropModalOpen(true);
  };

  const handleCropComplete = (croppedFile: File) => {
    const croppedPreview = URL.createObjectURL(croppedFile);
    setTempPreview(croppedPreview);
    setTempFile(croppedFile);
    setCropModalOpen(false);
  };

  const fetchUserData = async () => {
    try {
      const [attributes] = await Promise.all([fetchUserAttributes()]);

      setImage(attributes?.picture || "");

      const [nome, sobrenome] = (attributes?.name || "").trim().split(" ");
      setFirstName(nome || "");
      setLastName(sobrenome || "");
    } catch (error) {
      console.error("Erro ao buscar dados do usuário:", error);
    }
  };

  useEffect(() => {
    if (open) {
      fetchUserData();
    }
  }, [open]);

  const modalStyle = {
    position: "absolute" as const,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: 704,
    height: "auto",
    maxHeight: 800,
    bgcolor: "background.paper",
    borderRadius: "16px",
    boxShadow: 24,
    outline: "none",
    display: "flex",
    flexDirection: "column",
  };

  useEffect(() => {
    if (open) {
      setTempPreview(image);
      setTempFile(null);
      setTempFirstName(firstName);
      setTempLastName(lastName);
    }
  }, [open, image]);

  const uploadImage = async (): Promise<boolean> => {
    if (!tempFile) {
      console.error("Nenhuma imagem selecionada.");
      return false;
    }

    const session = await fetchAuthSession();
    const userSub = session?.userSub;

    if (!userSub) {
      console.error("Usuário não logado ou sub não encontrado.");
      return false;
    }

    try {
      const uploadResult = await uploadData({
        path: `${userSub}.png`,
        data: tempFile,
        options: {
          contentType: "image/png",
          bucket: { bucketName: "amazon-dispatcher", region: "us-east-1" },
        },
      });

      await uploadResult.result;

      console.log("Upload concluído!", uploadResult);
      return true;
    } catch (error) {
      console.error("Erro no upload:", error);
      return false;
    }
  };

  function getPublicUrl(bucketName: string, key: any) {
    const timestamp = new Date().getTime();
    return `https://${bucketName}.s3.us-east-1.amazonaws.com/${key}?t=${timestamp}`;
  }

  const handleSaveNewInfoUser = async (name: string): Promise<void> => {
    if (!tempFirstName.trim() || !tempLastName.trim()) {
      toast.error("Por favor, preencha o nome e o sobrenome.");
      return;
    }

    setIsSaving(true);

    try {
      let imageUrl = image;

      if (tempFile) {
        const uploadSuccess = await uploadImage();

        if (uploadSuccess === false) {
          toast.error("Falha ao fazer upload da imagem.");
          return;
        }

        const session = await fetchAuthSession();
        const userSub = session?.userSub;
        imageUrl = getPublicUrl("amazon-dispatcher", `${userSub}.png`);
      }

      await updateUserAttributes({
        userAttributes: {
          name: name,
          picture: imageUrl,
        },
      });

      setImage(imageUrl);
      setTempPreview(imageUrl);
      setFirstName(tempFirstName);
      setLastName(tempLastName);

      await getCurrentUser();
      console.log("Atributos atualizados com sucesso!");
      toast.success("Atributos atualizados com sucesso!");
      onClose();
    } catch (error) {
      console.error("Erro no processo editar usuário:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{ backdrop: { timeout: 300 } }}
    >
      <Fade in={open}>
        <Box sx={modalStyle}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 2,
              py: 1.5,
              backgroundColor: "#0A0C33",
              borderTopLeftRadius: "16px",
              borderTopRightRadius: "16px",
              position: "relative",
            }}
          >
            <Typography variant="h5" color="#fff">
              Editar Usuário
            </Typography>
            <IconButton
              onClick={onClose}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: "#fff",
                border: "1px solid white",
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              marginTop: "50px",
              gap: "30px",
            }}
          >
            <EditImagePicker
              imagePreview={tempPreview}
              onImageSelect={(file, preview) =>
                handleImageSelect(file, preview)
              }
            />
            <NomeSobrenomeForm
              nome={tempFirstName}
              sobrenome={tempLastName}
              onChangeNome={setTempFirstName}
              onChangeSobrenome={setTempLastName}
            />
            {cropModalOpen && (
              <CropperModal
                image={selectedImageForCrop}
                onCancel={() => {
                  setCropModalOpen(false);
                  setSelectedImageForCrop("");
                }}
                onCropComplete={handleCropComplete}
              />
            )}
          </div>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              p: 2,
              borderTop: "1px solid #eee",
            }}
          >
            <Button
              size="large"
              variant="contained"
              onClick={() => {
                const fullName = `${tempFirstName} ${tempLastName}`.trim();
                handleSaveNewInfoUser(fullName);
              }}
              sx={{
                textTransform: "none",
                backgroundColor: "#326AFD",
                borderRadius: "26px",
                fontWeight: 800,
              }}
              disabled={isSaving}
            >
              {isSaving ? (
                <CircularProgress size={24} sx={{ color: "#fff" }} />
              ) : (
                "Salvar"
              )}
            </Button>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};
